import React, { useRef, useState } from "react";
import { Fuel, Trash2, Camera, IndianRupee, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, today, formatApiError } from "../../lib/api";
import { AttachmentsDialog, AttachButton } from "../Attachments";
import { FuelPayDialog, FuelUpiSetting } from "./FuelPay";

const ctl = "h-9 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const blank = () => ({ vehicle_number: "", date: today(), litres: "", amount: "", odometer: "", note: "" });

const FuelForm = ({ vehicles, onSaved }) => {
  const [f, setF] = useState(blank());
  const [slip, setSlip] = useState(null);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!f.vehicle_number) return toast.error("Pick the vehicle");
    try {
      const { data } = await api.post("/gatepass/fuel", { ...f, litres: parseFloat(f.litres) || 0, amount: parseFloat(f.amount) || 0, odometer: f.odometer === "" ? null : parseFloat(f.odometer) });
      if (slip) {
        const fd = new FormData(); fd.append("file", slip);
        try { await api.post("/attachments", fd, { params: { entity_type: "fuel", entity_id: data.id }, headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e) { toast.error(`Fuel saved, slip not uploaded: ${formatApiError(e.response?.data?.detail)}`); }
      }
      toast.success("Fuel entry saved"); setF(blank()); setSlip(null); if (fileRef.current) fileRef.current.value = ""; onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2">
      <select data-testid="fuel-vehicle" className={ctl} value={f.vehicle_number} onChange={set("vehicle_number")}>
        <option value="">Vehicle…</option>
        {vehicles.map((v) => <option key={v.number} value={v.number}>{v.number}</option>)}
      </select>
      <input data-testid="fuel-date" type="date" className={`${ctl} num`} value={f.date} onChange={set("date")} />
      <input data-testid="fuel-litres" type="number" min="0" step="0.01" className={`${ctl} w-24 num`} placeholder="Litres" value={f.litres} onChange={set("litres")} />
      <input data-testid="fuel-amount" type="number" min="0" step="0.01" className={`${ctl} w-24 num`} placeholder="₹ Amount" value={f.amount} onChange={set("amount")} />
      <input data-testid="fuel-odometer" type="number" min="0" className={`${ctl} w-28 num`} placeholder="Odometer km" value={f.odometer} onChange={set("odometer")} />
      <input data-testid="fuel-note" className={`${ctl} w-36`} placeholder="Note / pump" value={f.note} onChange={set("note")} />
      <label data-testid="fuel-slip" className={`${ctl} flex items-center gap-1.5 cursor-pointer max-w-[180px] ${slip ? "border-primary text-primary" : "text-muted-foreground"}`}>
        <Camera size={14} /><span className="truncate text-xs">{slip ? slip.name : "Fuel slip photo"}</span>
        <input ref={fileRef} data-testid="fuel-slip-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setSlip(e.target.files[0] || null)} />
      </label>
      <button data-testid="fuel-save" onClick={save} className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 hover:bg-primary/90 transition-colors"><Fuel size={13} /> Add fuel</button>
    </div>
  );
};

const UnpaidTotal = ({ entries }) => {
  const due = entries.filter((r) => r.amount > 0 && !r.paid);
  const total = due.reduce((s, r) => s + r.amount, 0);
  return (
    <div data-testid="fuel-unpaid-total" className={`rounded-lg border px-4 py-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 ${due.length ? "border-destructive/40 bg-destructive/10" : "border-emerald-500/40 bg-emerald-500/10"}`}>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unpaid fuel</span>
      <span data-testid="fuel-unpaid-amount" className={`num text-xl font-bold ${due.length ? "text-destructive" : "text-emerald-600"}`}>{inr(total)}</span>
      <span data-testid="fuel-unpaid-count" className="text-xs text-muted-foreground">{due.length ? `${due.length} entr${due.length === 1 ? "y" : "ies"} to pay in this period` : "All fuel in this period is paid"}</span>
    </div>
  );
};

const PayCell = ({ r, canWrite, onPay, onView }) => {
  if (r.paid) return (
    <button data-testid={`fuel-paid-${r.id}`} onClick={onView} title={`Paid by ${r.paid_by_name}${r.upi_ref ? ` · Ref ${r.upi_ref}` : ""}`} className="text-xs font-semibold text-emerald-600 flex items-center gap-1 hover:underline"><CheckCircle2 size={13} /> Paid</button>
  );
  if (!canWrite || !r.amount) return <span className="text-xs text-muted-foreground">{r.amount ? "Unpaid" : ""}</span>;
  return <button data-testid={`fuel-pay-${r.id}`} onClick={onPay} className="h-7 px-2 rounded-md border border-primary text-primary text-xs font-medium flex items-center gap-1 hover:bg-primary/10 transition-colors"><IndianRupee size={12} /> Pay UPI</button>;
};

export const FuelLog = ({ vehicles, entries, canWrite, canDelete, isAdmin, upi, onUpiSaved, onChanged }) => {
  const [att, setAtt] = useState(null);
  const [pay, setPay] = useState(null);
  const del = async (r) => {
    if (!window.confirm(`Delete fuel entry ${r.litres} L for ${r.vehicle_number} on ${r.date}?`)) return;
    try { await api.delete(`/gatepass/fuel/${r.id}`); toast.success("Fuel entry deleted"); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <section data-testid="fuel-log" className="rounded-xl border border-border bg-card p-4 mt-6 space-y-3">
      <div>
        <h3 className="font-display text-base md:text-lg font-semibold flex items-center gap-2"><Fuel size={16} /> Fuel filled</h3>
        <p className="text-xs text-muted-foreground">Enter the odometer at each fill to see fill-to-fill km per litre. Fuel is only recorded here, not in the cash book.</p>
      </div>
      <UnpaidTotal entries={entries} />
      {isAdmin ? <FuelUpiSetting upi={upi} onSaved={onUpiSaved} /> : upi.upi_id ? <div className="text-xs text-muted-foreground">Fuel pump UPI: <b className="num">{upi.name ? `${upi.name} · ` : ""}{upi.upi_id}</b></div> : null}
      {canWrite && <FuelForm vehicles={vehicles} onSaved={onChanged} />}
      {entries.length === 0 ? <div className="text-sm text-muted-foreground py-4 text-center">No fuel entries in this period.</div> : (
        <div className="divide-y divide-border">
          {entries.map((r) => (
            <div key={r.id} data-testid={`fuel-row-${r.id}`} className={`grid grid-cols-2 md:grid-cols-[100px_110px_90px_100px_110px_130px_minmax(0,1fr)_80px_40px_30px] gap-2 md:gap-4 py-2.5 text-sm items-center ${r.low_mileage ? "bg-destructive/10 -mx-2 px-2 rounded" : ""}`}>
              <span className="num text-xs">{r.date}</span>
              <span className="num font-semibold">{r.vehicle_number}</span>
              <span className="num font-bold">{r.litres} L</span>
              <span className="num text-xs">{r.amount ? inr(r.amount) : "—"}</span>
              <span className="num text-xs">Odo {r.odometer ?? "—"}</span>
              <span data-testid={`fuel-kmpl-${r.id}`} className={`num text-xs font-semibold ${r.low_mileage ? "text-destructive" : ""}`} title={r.usual_kmpl ? `Usual ${r.usual_kmpl} km/L` : ""}>{r.km_per_litre === null ? "—" : `${r.km_per_litre} km/L`}{r.low_mileage ? " · LOW" : ""}</span>
              <span className="text-xs text-muted-foreground break-words">{r.note}{r.created_by_name ? ` · ${r.created_by_name}` : ""}</span>
              <PayCell r={r} canWrite={canWrite} onPay={() => setPay(r)} onView={() => setAtt(r)} />
              <AttachButton testid={`fuel-att-${r.id}`} count={r.attachment_count} onClick={() => setAtt(r)} />
              {canDelete ? <button data-testid={`fuel-del-${r.id}`} onClick={() => del(r)} className="text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button> : <span />}
            </div>
          ))}
        </div>
      )}
      <FuelPayDialog r={pay} upi={upi} onClose={() => setPay(null)} onPaid={() => { setPay(null); onChanged(); }} />
      <AttachmentsDialog open={!!att} onClose={() => setAtt(null)} entityType="fuel" entityId={att?.id} title={att ? `Fuel slip · ${att.vehicle_number} · ${att.date}` : ""} onChanged={onChanged} />
    </section>
  );
};
